/**
 * THESIS: Curiosity Engine makes engineering judgement explorable and refuses the static portfolio grid.
 * OWN-WORLD: A daylight WebGL thought machine uses responsive topology, saturated kinetic materials, and stable semantic type.
 * STORY: Visitors meet Johnny's thesis, manipulate the system, inspect an AI feature pipeline and case-study shells, then contact him.
 * FIRST VIEWPORT: The living core owns the frame; Johnny's name, thesis, wake action, visible index, and contact path remain immediately legible.
 * FORM: User-pinned continuous explorable world, chosen after seed 63606336; staging keeps one persistent world that transforms into each surface.
 */
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { AnimatePresence } from "motion/react";
import { DetailPanel } from "./components/DetailPanel";
import { ExperienceErrorBoundary } from "./components/ExperienceErrorBoundary";
import { HomeIntroduction } from "./components/HomeIntroduction";
import { LoadingExperience } from "./components/LoadingExperience";
import { ReadingView } from "./components/ReadingView";
import { SiteHeader } from "./components/SiteHeader";
import { WebGLFallback } from "./components/WebGLFallback";
import { WorldIndex } from "./components/WorldIndex";
import { useHashRoute } from "./hooks/useHashRoute";
import { useJourneyNavigation } from "./hooks/useJourneyNavigation";
import { useAppearance } from "./hooks/useAppearance";
import { useReducedMotion } from "./hooks/useReducedMotion";
import { useWebGLSupport } from "./hooks/useWebGLSupport";
import { normalizeHash, routeToWorldEntry } from "./lib/navigation";
import { usePortfolioStore } from "./state/usePortfolioStore";

const CuriosityScene = lazy(() => import("./experience/CuriosityScene"));

export default function App() {
  const route = useHashRoute();
  const reducedMotion = useReducedMotion();
  const webGLSupported = useWebGLSupport();
  const { preference, resolvedTheme, cycleAppearance } = useAppearance();
  const [sceneFailed, setSceneFailed] = useState(false);
  const markVisited = usePortfolioStore((state) => state.markVisited);
  const activeEntry = routeToWorldEntry(route);
  const journey = useJourneyNavigation(route);
  const routePath = normalizeHash(window.location.hash);
  const previousRoutePath = useRef(routePath);

  useEffect(() => {
    const current = normalizeHash(window.location.hash);
    if (current !== "/") markVisited(current);
  }, [markVisited, route]);

  useEffect(() => {
    document.title =
      route.kind === "read"
        ? "Johnny Leek | Engineering portfolio"
        : "Johnny Leek | Software Engineer II · AI systems";
    if (previousRoutePath.current === routePath) return;
    previousRoutePath.current = routePath;
    const frame = requestAnimationFrame(() => {
      document.getElementById("main-content")?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [routePath, route.kind]);

  const skipToContent = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById("main-content")?.focus();
  };

  return (
    <div className="app-shell" data-route={route.kind}>
      <a className="skip-link" href="#main-content" onClick={skipToContent}>
        Skip to portfolio content
      </a>

      {route.kind !== "read" ? (
        <div className="world-layer">
          {webGLSupported && !sceneFailed ? (
            <div className="canvas-experience" aria-hidden="true">
              <ExperienceErrorBoundary onError={() => setSceneFailed(true)}>
                <Suspense fallback={<LoadingExperience />}>
                  <CuriosityScene
                    activeEntry={activeEntry}
                    reducedMotion={reducedMotion}
                    theme={resolvedTheme}
                  />
                </Suspense>
              </ExperienceErrorBoundary>
            </div>
          ) : (
            <WebGLFallback
              reason={webGLSupported ? "render-error" : "unsupported"}
            />
          )}
        </div>
      ) : null}

      <SiteHeader
        route={route}
        appearancePreference={preference}
        resolvedTheme={resolvedTheme}
        onCycleAppearance={cycleAppearance}
      />

      <AnimatePresence mode="wait">
        {route.kind === "home" ? (
          <HomeIntroduction
            key="home"
            journey={journey}
            reducedMotion={reducedMotion}
          />
        ) : null}
        {route.kind === "index" ? <WorldIndex key="index" /> : null}
        {route.kind === "read" ? <ReadingView key="read" /> : null}
      </AnimatePresence>

      <DetailPanel journey={journey} route={route} />

      {route.kind !== "read" ? (
        <aside
          className="interaction-guide"
          aria-label="Interaction instructions"
        >
          <span aria-hidden="true" />
          <p>
            <strong>Drag</strong> to orbit
            <strong>Scroll</strong> to move
            <strong>Choose</strong> a structure to inspect
          </p>
        </aside>
      ) : null}
    </div>
  );
}
